import moment from 'moment';

export const MAX_SFTP_DESTINATIONS = 10;

const DEFAULT_DATE_FORMAT = 'YYYYMMDD';
const DEFAULT_DAILY_REPORT_NAME = 'Report';

export const emptySftpDestination = () => ({
  client_sftp_connection_id: '',
  acknowledgement: null,
  daily_report: null,
});

export const emptyAcknowledgementDelivery = () => ({
  file_path: '',
  file_name: '',
  use_original_filename: false,
});

export const emptyDailyReportDelivery = () => ({
  file_location: '',
  file_name: '',
});

const blankToNull = (value) =>
  value === '' || value === undefined ? null : value;

export const fromSftpDestinationsResponse = (destinations = []) =>
  destinations.map(
    ({ client_sftp_connection_id, acknowledgement, daily_report }) => ({
      client_sftp_connection_id: client_sftp_connection_id ?? '',
      acknowledgement: acknowledgement
        ? {
            file_path: acknowledgement.file_path ?? '',
            file_name: acknowledgement.file_name ?? '',
            use_original_filename: !!acknowledgement.use_original_filename,
          }
        : null,
      daily_report: daily_report
        ? {
            file_location: daily_report.file_location ?? '',
            file_name: daily_report.file_name ?? '',
          }
        : null,
    }),
  );

export const toSftpDestinationsPayload = (destinations = []) =>
  destinations.map(
    ({ client_sftp_connection_id, acknowledgement, daily_report }) => ({
      client_sftp_connection_id:
        client_sftp_connection_id === '' ? null : client_sftp_connection_id,
      acknowledgement: acknowledgement
        ? {
            file_path: acknowledgement.file_path,
            file_name: blankToNull(acknowledgement.file_name),
            use_original_filename: !!acknowledgement.use_original_filename,
          }
        : null,
      daily_report: daily_report
        ? {
            file_location: daily_report.file_location,
            file_name: blankToNull(daily_report.file_name),
          }
        : null,
    }),
  );

const isDirectory = (value) =>
  typeof value === 'string' && value.startsWith('/') && value.endsWith('/');

export const validateSftpDestinations = (
  destinations,
  { outboundReporting, generateAcknowledgementFile, generateDailyReport },
  t,
) => {
  const errors = {};

  destinations.forEach((destination, index) => {
    const path = `sftp_destinations[${index}]`;
    const { acknowledgement, daily_report: dailyReport } = destination;

    if (!destination.client_sftp_connection_id) {
      errors[`${path}.client_sftp_connection_id`] = t(
        'dataMappings.sftpDestinations.errors.connectionRequired',
      );
    }
    if (!acknowledgement && !dailyReport) {
      errors[path] = t('dataMappings.sftpDestinations.errors.fileRequired');
    }
    if (acknowledgement) {
      if (!isDirectory(acknowledgement.file_path)) {
        errors[`${path}.acknowledgement.file_path`] = t(
          'dataMappings.sftpDestinations.errors.folderFormat',
        );
      }
      if (
        generateAcknowledgementFile &&
        !acknowledgement.file_name &&
        !acknowledgement.use_original_filename
      ) {
        errors[`${path}.acknowledgement.file_name`] = t(
          'dataMappings.sftpDestinations.errors.acknowledgementNameRequired',
        );
      }
    }
    if (dailyReport) {
      if (!isDirectory(dailyReport.file_location)) {
        errors[`${path}.daily_report.file_location`] = t(
          'dataMappings.sftpDestinations.errors.folderFormat',
        );
      }
      if (generateDailyReport && !dailyReport.file_name) {
        errors[`${path}.daily_report.file_name`] = t(
          'dataMappings.sftpDestinations.errors.dailyReportNameRequired',
        );
      }
    }
  });

  if (outboundReporting && destinations.length === 0) {
    errors.sftp_destinations = t(
      'dataMappings.sftpDestinations.errors.destinationRequired',
    );
  } else if (
    generateAcknowledgementFile &&
    !destinations.some((destination) => destination.acknowledgement)
  ) {
    errors.sftp_destinations = t(
      'dataMappings.sftpDestinations.errors.acknowledgementDestinationRequired',
    );
  } else if (
    generateDailyReport &&
    !destinations.some((destination) => destination.daily_report)
  ) {
    errors.sftp_destinations = t(
      'dataMappings.sftpDestinations.errors.dailyReportDestinationRequired',
    );
  }

  return errors;
};

const fileDate = (dateFormat) =>
  moment().format(dateFormat || DEFAULT_DATE_FORMAT);

export const previewAcknowledgementFileName = (
  acknowledgement,
  { dateFormat, fileExtension, originalFileLabel },
) => {
  const prefix = acknowledgement.file_name;
  const base = acknowledgement.use_original_filename
    ? originalFileLabel
    : fileDate(dateFormat);
  return `${prefix ? `${prefix}_` : ''}${base}${fileExtension || ''}`;
};

export const previewDailyReportFileName = (
  dailyReport,
  { dateFormat, fileExtension },
) =>
  `${dailyReport.file_name || DEFAULT_DAILY_REPORT_NAME}_${fileDate(
    dateFormat,
  )}${fileExtension || ''}`;
